import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  app,
  cacheHomepageSnapshot,
  buildHomepageSnapshotFromProfile,
  cacheProfileDocumentsSnapshot,
  cacheUserProfileSnapshot,
  canAccessProfileFromProfile,
  completeFlowEmailPasswordAuth,
  completeFlowSignInWithApple,
  completeFlowSignInWithGoogle,
  consumePendingAuthRedirectResult,
  consumePendingEmailLinkSignIn,
  completePendingEmailLinkSignIn,
  createSubscriptionCheckoutSession,
  createBillingPortalSession,
  ensureUserProfileDocument,
  getAnalyticsEventContext,
  getCurrentUser,
  getUiBootstrapSnapshot,
  isSubscriptionActiveFromProfile,
  logAnalyticsEvent,
  openDeferredExternalUrl,
  onAuthUserChanged,
  sendEmailSignInLinkForFlow,
  signOutCurrentUser,
  startDailyVideoGeneration,
  shouldPreferMagicLinkEmailAuth,
  subscribeToUserProfile,
} from './Firebase';
import AppHeader, { appHeaderActionClasses } from './AppHeader';
import AuthGateStep from './UploadPhotos/AuthGateStep.jsx';
import LegalFooter from './LegalFooter';
import {
  formatNextDailyAvailability,
  getNextDailyAvailableAtMillis,
  isDailyResultFromToday,
} from './homeState.js';
import { getModernResultPath, getResultPathForDocument } from './resultPaths.js';

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
};

const formatProfileDate = (timestamp) => {
  const millis = toMillis(timestamp);
  if (!millis) return 'Unknown date';
  return new Date(millis).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const isUnlocked = (docData, subscriptionActive) => {
  const unlockSource = String(docData?.unlock_source || '').toLowerCase();
  if (unlockSource === 'subscription') {
    if (typeof docData?.subscription_access_active === 'boolean') {
      return docData.subscription_access_active;
    }
    return Boolean(subscriptionActive);
  }

  return Boolean(
    docData?.has_paid ||
    docData?.payment_status === 'paid' ||
    docData?.is_unlocked ||
    docData?.purchase_unlocked ||
    subscriptionActive
  );
};

const getPreviewVideoUrl = (docData) => {
  const videos = Array.isArray(docData?.output_videos)
    ? docData.output_videos.filter((url) => typeof url === 'string' && url.trim())
    : [];
  if (videos.length > 0) {
    return videos[0];
  }
  if (typeof docData?.preview_video_url === 'string' && docData.preview_video_url.trim()) {
    return docData.preview_video_url.trim();
  }
  return '';
};

const isProfileVisibleDocument = (docData) => {
  const status = String(docData?.generation_status || '').toLowerCase();
  return !['failed', 'error', 'cancelled', 'canceled'].includes(status);
};

const getLatestCompletedResultDocId = (documents) => {
  const completedDocuments = Array.isArray(documents)
    ? documents.filter((docData) => {
      const status = String(docData?.generation_status || '').toLowerCase();
      return status === 'completed' && Boolean(getPreviewVideoUrl(docData));
    })
    : [];

  if (completedDocuments.length === 0) {
    return '';
  }

  const [latestDocument] = completedDocuments.sort(
    (left, right) => toMillis(right?.timestamp) - toMillis(left?.timestamp)
  );
  return typeof latestDocument?.id === 'string' ? latestDocument.id : '';
};

const buildPendingDailyPlaceholderDoc = (docId, profileData) => ({
  id: docId,
  generation_status: String(profileData?.daily_generation_status || 'in_progress'),
  timestamp: profileData?.updated_at || Date.now(),
  is_daily_generation: true,
  is_pending_placeholder: true,
});

const PROFILE_PAGE_SIZE = 6;
const primaryProfileActionClasses =
  'inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_-20px_rgba(244,114,182,0.75)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_55px_-22px_rgba(244,114,182,0.8)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0';
const DEV_PROFILE_STATE_PARAM = 'devProfileState';

const ProfileLoadingCard = () => (
  <div className="rounded-[28px] border border-white/70 bg-white/90 px-8 py-10 text-center shadow-[0_24px_70px_-36px_rgba(244,114,182,0.45)] backdrop-blur-xl">
    <div className="flex flex-col items-center justify-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 shadow-inner">
        <div className="h-11 w-11 rounded-full border-[3px] border-rose-100 border-t-pink-500 border-r-orange-400 animate-spin shadow-[0_0_24px_rgba(244,114,182,0.18)]" />
      </div>
    </div>
  </div>
);

const getDevProfileState = (search) => {
  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  const nextValue = new URLSearchParams(search).get(DEV_PROFILE_STATE_PARAM);
  return String(nextValue || '').trim().toLowerCase();
};

const buildMockProfileDocument = (docId, daysAgo = 0) => {
  const timestamp = Date.now() - (daysAgo * 24 * 60 * 60 * 1000);
  return {
    id: docId,
    generation_status: 'completed',
    unlock_source: 'subscription',
    subscription_access_active: true,
    output_videos: ['https://storage.googleapis.com/falserverless/example_inputs/sample-video.mp4'],
    timestamp,
  };
};

const applyDevProfileState = ({ devProfileState, profile, documents }) => {
  if (!devProfileState) {
    return {
      profile,
      documents,
      bannerLabel: '',
    };
  }

  const baseProfile = profile ? { ...profile } : {};
  const fallbackDocuments = Array.isArray(documents) ? [...documents] : [];
  const firstDocumentId = String(
    fallbackDocuments[0]?.id ||
    baseProfile?.latest_result_doc_id ||
    baseProfile?.daily_last_doc_id ||
    'mock-profile-doc'
  ).trim();
  const baseDocuments = fallbackDocuments.length > 0
    ? fallbackDocuments
    : [buildMockProfileDocument(firstDocumentId)];

  if (devProfileState === 'first-run') {
    return {
      profile: {
        ...baseProfile,
        home_state: 'signed_in_default',
        home_action_doc_id: '',
        latest_result_doc_id: '',
        latest_primary_result_doc_id: '',
        result_doc_ids: [],
        daily_generation_status: '',
        daily_active_doc_id: '',
        daily_last_doc_id: '',
        daily_last_completed_date: '',
        has_completed_first_set: false,
      },
      documents: [],
      bannerLabel: 'First run',
    };
  }

  if (devProfileState === 'daily-available') {
    return {
      profile: {
        ...baseProfile,
        subscription_active: true,
        subscription_status: 'active',
        home_state: 'daily_available',
        home_action_doc_id: firstDocumentId,
        latest_result_doc_id: baseProfile?.latest_result_doc_id || firstDocumentId,
        latest_primary_result_doc_id: baseProfile?.latest_primary_result_doc_id || firstDocumentId,
        result_doc_ids: Array.from(new Set([
          ...(Array.isArray(baseProfile?.result_doc_ids) ? baseProfile.result_doc_ids : []),
          ...baseDocuments.map((docData) => docData.id),
        ])),
        has_completed_first_set: true,
        daily_generation_status: '',
        daily_active_doc_id: '',
        daily_last_doc_id: '',
        daily_last_completed_date: '',
      },
      documents: baseDocuments,
      bannerLabel: 'Daily available',
    };
  }

  if (devProfileState === 'daily-generating') {
    return {
      profile: {
        ...baseProfile,
        subscription_active: true,
        subscription_status: 'active',
        home_state: 'daily_generating',
        home_action_doc_id: firstDocumentId,
        latest_result_doc_id: baseProfile?.latest_result_doc_id || firstDocumentId,
        latest_primary_result_doc_id: baseProfile?.latest_primary_result_doc_id || firstDocumentId,
        result_doc_ids: Array.from(new Set([
          ...(Array.isArray(baseProfile?.result_doc_ids) ? baseProfile.result_doc_ids : []),
          ...baseDocuments.map((docData) => docData.id),
        ])),
        has_completed_first_set: true,
        daily_generation_status: 'processing',
        daily_active_doc_id: firstDocumentId,
      },
      documents: baseDocuments,
      bannerLabel: 'Daily generating',
    };
  }

  if (devProfileState === 'daily-ready') {
    const nextAvailableAtMillis = new Date().setHours(17, 0, 0, 0);
    return {
      profile: {
        ...baseProfile,
        subscription_active: true,
        subscription_status: 'active',
        home_state: 'daily_ready',
        home_action_doc_id: firstDocumentId,
        latest_result_doc_id: baseProfile?.latest_result_doc_id || firstDocumentId,
        latest_primary_result_doc_id: baseProfile?.latest_primary_result_doc_id || firstDocumentId,
        result_doc_ids: Array.from(new Set([
          ...(Array.isArray(baseProfile?.result_doc_ids) ? baseProfile.result_doc_ids : []),
          ...baseDocuments.map((docData) => docData.id),
        ])),
        has_completed_first_set: true,
        daily_generation_status: '',
        daily_active_doc_id: '',
        daily_last_doc_id: firstDocumentId,
        daily_last_completed_date: new Date().toISOString().slice(0, 10),
        daily_next_available_at_ms: nextAvailableAtMillis,
      },
      documents: baseDocuments,
      bannerLabel: 'Daily already used',
    };
  }

  return {
    profile,
    documents,
    bannerLabel: '',
  };
};

const MyResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bootstrapSnapshot] = useState(() => getUiBootstrapSnapshot());
  const [authUser, setAuthUser] = useState(() => bootstrapSnapshot.authUser || getCurrentUser());
  const [authLoading, setAuthLoading] = useState(
    () => !(getCurrentUser() && !getCurrentUser().isAnonymous)
  );
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isManagingBilling, setIsManagingBilling] = useState(false);
  const [isStartingSubscription, setIsStartingSubscription] = useState(false);
  const [isStartingDailyVideo, setIsStartingDailyVideo] = useState(false);
  const [userProfile, setUserProfile] = useState(() => bootstrapSnapshot.userProfile);
  const [userProfileResolved, setUserProfileResolved] = useState(
    () => !bootstrapSnapshot.authUser || bootstrapSnapshot.hasCachedProfile
  );
  const [documents, setDocuments] = useState(() => bootstrapSnapshot.documents);
  const [allDocuments, setAllDocuments] = useState(() => bootstrapSnapshot.documents);
  const [documentsResolved, setDocumentsResolved] = useState(
    () => !bootstrapSnapshot.authUser || bootstrapSnapshot.hasCachedDocuments
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [emailLinkPending, setEmailLinkPending] = useState(false);
  const [profileViewLogged, setProfileViewLogged] = useState(false);
  const authRedirectHandledRef = useRef(false);
  const prefersMagicLinkEmailAuth = shouldPreferMagicLinkEmailAuth();
  const devProfileState = useMemo(
    () => getDevProfileState(location.search),
    [location.search]
  );
  const bypassProfileGuard = Boolean(devProfileState);

  const trackProfileEvent = useCallback((eventName, properties = {}, userOverride = null) => {
    const analyticsContext = getAnalyticsEventContext(userOverride || authUser);
    logAnalyticsEvent({
      analyticsContext,
      eventName,
      properties: {
        screen: 'profile',
        ...properties,
      },
    });
  }, [authUser]);

  useEffect(() => {
    const unsubscribe = onAuthUserChanged((user) => {
      setAuthUser(user && !user.isAnonymous ? user : null);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authLoading) {
      return () => {};
    }

    if (!authUser) {
      setUserProfile(null);
      setUserProfileResolved(true);
      return () => {};
    }

    ensureUserProfileDocument(authUser).catch((profileError) => {
      console.error('Failed to ensure user profile:', profileError);
    });

    setUserProfileResolved(false);
    return subscribeToUserProfile(authUser.uid, (profileData) => {
      setUserProfile(profileData || null);
      setUserProfileResolved(true);
      cacheUserProfileSnapshot(authUser, profileData || null);
    });
  }, [authLoading, authUser]);

  const loadDocuments = useCallback(async (user, profileOverride = null) => {
    if (!user) {
      setDocuments([]);
      setAllDocuments([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const db = getFirestore(app);
      const merged = new Map();
      const profile = profileOverride || userProfile;
      const profileDocIds = Array.isArray(profile?.result_doc_ids)
        ? profile.result_doc_ids.filter((id) => typeof id === 'string' && id.trim())
        : [];

      if (profileDocIds.length > 0) {
        const profileDocs = await Promise.all(
          profileDocIds.map((id) => getDoc(doc(db, 'petPhotos', id)))
        );
        profileDocs.forEach((docSnap) => {
          if (docSnap.exists()) {
            merged.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          }
        });
      }

      const snapshots = [];
      snapshots.push(getDocs(query(collection(db, 'petPhotos'), where('owner_uid', '==', user.uid))));

      const normalizedEmail = (user.email || '').trim().toLowerCase();
      if (normalizedEmail) {
        snapshots.push(getDocs(query(collection(db, 'petPhotos'), where('owner_email', '==', normalizedEmail))));
        snapshots.push(getDocs(query(collection(db, 'petPhotos'), where('email', '==', normalizedEmail))));
      }

      const results = await Promise.all(snapshots);
      results.forEach((snapshot) => {
        snapshot.forEach((docSnap) => {
          merged.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      });

      const sorted = Array.from(merged.values()).sort(
        (a, b) => toMillis(b.timestamp) - toMillis(a.timestamp)
      );
      setAllDocuments(sorted);
      const visibleDocuments = sorted.filter(isProfileVisibleDocument);
      setDocuments(visibleDocuments);
      cacheProfileDocumentsSnapshot(user, visibleDocuments);
      const latestCompletedResultDocId = getLatestCompletedResultDocId(visibleDocuments);
      const profileSubscriptionActive = isSubscriptionActiveFromProfile(profile);
      const explicitHomeState = String(profile?.home_state || '').trim();
      if (explicitHomeState) {
        cacheHomepageSnapshot(
          user,
          buildHomepageSnapshotFromProfile(user, profile, bootstrapSnapshot.homepage)
        );
      } else {
        const fallbackHeroState = !profileSubscriptionActive
          ? 'signed_in_default'
          : latestCompletedResultDocId
            ? 'daily_available'
            : 'subscription_active_no_results';
        cacheHomepageSnapshot(user, {
          hasCompletedResult: Boolean(latestCompletedResultDocId),
          latestResultDocId: String(profile?.latest_result_doc_id || latestCompletedResultDocId || '').trim(),
          homeActionDocId: String(
            profile?.latest_primary_result_doc_id ||
            profile?.latest_result_doc_id ||
            latestCompletedResultDocId ||
            ''
          ).trim(),
          subscriptionActive: profileSubscriptionActive,
          canCreateDailyFromHome: fallbackHeroState === 'daily_available',
          heroState: fallbackHeroState,
        });
      }
    } catch (loadError) {
      console.error('Failed to load user results:', loadError);
      setError('Could not load your results. Please refresh and try again.');
    } finally {
      setLoading(false);
      setDocumentsResolved(true);
    }
  }, [bootstrapSnapshot.homepage, userProfile]);

  const realProfileAccessible = useMemo(
    () => (
      canAccessProfileFromProfile(userProfile) ||
      documents.length > 0 ||
      (Array.isArray(bootstrapSnapshot.documents) && bootstrapSnapshot.documents.length > 0)
    ),
    [bootstrapSnapshot.documents, documents.length, userProfile]
  );

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!authUser) {
      setDocuments([]);
      setAllDocuments([]);
      setDocumentsResolved(true);
      return;
    }

    if (!userProfileResolved) {
      return;
    }

    setDocumentsResolved(false);
    loadDocuments(authUser, userProfile);
  }, [authLoading, authUser, userProfileResolved, loadDocuments, userProfile]);

  useEffect(() => {
    if (authLoading || !authUser || !userProfileResolved || !documentsResolved || bypassProfileGuard) {
      return;
    }

    if (!realProfileAccessible) {
      navigate('/', { replace: true });
    }
  }, [authLoading, authUser, bypassProfileGuard, documentsResolved, navigate, realProfileAccessible, userProfileResolved]);

  const handleAuthSuccess = useCallback(async (user, source) => {
    setAuthUser(user);
    trackProfileEvent('fluffball-sign-in-succeeded', { source }, user);
  }, [trackProfileEvent]);

  const restorePendingProfileRedirect = useCallback(async (isMountedRef = { current: true }) => {
    const emailLinkResult = await consumePendingEmailLinkSignIn();
    if (!isMountedRef.current) {
      return;
    }

    if (emailLinkResult && emailLinkResult.flow === 'profile') {
      const source = 'profile_email_link';

      if (emailLinkResult.status === 'success' && emailLinkResult.user) {
        setEmailLinkPending(false);
        setAuthInfo('');
        setError('');
        await handleAuthSuccess(emailLinkResult.user, source);
        if (isMountedRef.current) {
          setIsSigningIn(false);
        }
        return;
      }

      setIsSigningIn(false);

      if (emailLinkResult.status === 'awaiting_email') {
        setEmailLinkPending(true);
        setError('');
        setAuthInfo('Enter the email address you used for the sign-in link to finish signing in.');
        return;
      }

      setEmailLinkPending(true);
      setAuthInfo('');
      if (emailLinkResult.status === 'error') {
        console.error('Profile email-link sign-in failed:', emailLinkResult.error);
        trackProfileEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(emailLinkResult.error?.message || emailLinkResult.error),
        });
        setError(String(emailLinkResult.error?.message || emailLinkResult.error || 'Could not sign in right now. Please try again.'));
      }
      return;
    }

    const redirectResult = await consumePendingAuthRedirectResult();
    if (!isMountedRef.current || !redirectResult || redirectResult.flow !== 'profile') {
      return;
    }

    const source = redirectResult.providerId === 'apple' ? 'profile_apple' : 'profile_google';

    if (redirectResult.status === 'success' && redirectResult.user) {
      await handleAuthSuccess(redirectResult.user, source);
      if (isMountedRef.current) {
        setIsSigningIn(false);
      }
      return;
    }

    if (redirectResult.status === 'error') {
      console.error('Profile redirect sign-in failed:', redirectResult.error);
      trackProfileEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(redirectResult.error?.message || redirectResult.error),
      });
      setError(String(redirectResult.error?.message || redirectResult.error || 'Could not sign in right now. Please try again.'));
    } else if (redirectResult.status === 'cancelled') {
      setError('Sign-in was cancelled. Please try again.');
    }

    setIsSigningIn(false);
  }, [handleAuthSuccess, trackProfileEvent]);

  useEffect(() => {
    const isMountedRef = { current: true };
    const runRestore = () => {
      void restorePendingProfileRedirect(isMountedRef);
    };

    if (!authRedirectHandledRef.current) {
      authRedirectHandledRef.current = true;
      runRestore();
    }

    const handlePageShow = () => {
      runRestore();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [restorePendingProfileRedirect]);

  const handleAuthAction = async ({ action, source, clearEmailLink = true }) => {
    if (isSigningIn) {
      return;
    }

    setIsSigningIn(true);
    setError('');
    setAuthInfo('');
    if (clearEmailLink) {
      setEmailLinkPending(false);
    }
    trackProfileEvent('fluffball-sign-in-clicked', { source });

    try {
      const user = await action();
      if (!user) {
        return;
      }
      await handleAuthSuccess(user, source);
    } catch (signInError) {
      console.error('Sign-in failed:', signInError);
      trackProfileEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(signInError?.message || signInError),
      });
      setError(String(signInError?.message || signInError || 'Could not sign in right now. Please try again.'));
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = () => handleAuthAction({
    action: () => completeFlowSignInWithGoogle({ redirectFlow: 'profile' }),
    source: 'profile_google',
  });

  const handleAppleSignIn = () => handleAuthAction({
    action: () => completeFlowSignInWithApple({ redirectFlow: 'profile' }),
    source: 'profile_apple',
  });

  const handleEmailCreate = ({ email, password }) => handleAuthAction({
    action: () => completeFlowEmailPasswordAuth({
      email,
      password,
      mode: 'create',
    }),
    source: 'profile_email_create',
  });

  const handleEmailSignIn = ({ email, password }) => handleAuthAction({
    action: () => completeFlowEmailPasswordAuth({
      email,
      password,
      mode: 'sign_in',
    }),
    source: 'profile_email_sign_in',
  });

  const handleEmailLinkSend = ({ email }) => handleAuthAction({
    action: async () => {
      await sendEmailSignInLinkForFlow({
        email,
        flow: 'profile',
      });
      setAuthInfo('Check your email for a sign-in link. If it opens in another browser, enter the same email here to finish.');
      return null;
    },
    source: 'profile_email_link',
  });

  const handleEmailLinkComplete = ({ email }) => handleAuthAction({
    action: async () => {
      const result = await completePendingEmailLinkSignIn({ email });
      if (result?.status !== 'success' || !result.user) {
        throw result?.error || new Error('This sign-in link is invalid or has already been used.');
      }
      setEmailLinkPending(false);
      return result.user;
    },
    source: 'profile_email_link',
    clearEmailLink: false,
  });

  const handleSignOut = async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    try {
      trackProfileEvent('fluffball-sign-out-clicked', {
        source: 'profile',
      });
      await signOutCurrentUser();
      setAuthUser(null);
      setDocuments([]);
      navigate('/', { replace: true });
    } catch (signOutError) {
      console.error('Sign-out failed:', signOutError);
      setError('Could not sign out right now.');
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleManageSubscription = async () => {
    if (isManagingBilling) {
      return;
    }

    const stripeCustomerId = String(userProfile?.stripe_customer_id || '').trim();
    if (!stripeCustomerId) {
      setError('No subscription customer record found for this account yet.');
      return;
    }

    setIsManagingBilling(true);
    setError('');
    trackProfileEvent('fluffball-manage-subscription-clicked');
    try {
      const billingUrl = await openDeferredExternalUrl(async () => {
        const billingSession = await createBillingPortalSession({
          stripeCustomerId,
          returnUrl: `${window.location.origin}/profile`,
        });
        return billingSession.url;
      });
      trackProfileEvent('fluffball-manage-subscription-opened');
      if (!billingUrl) {
        throw new Error('Billing portal URL was not returned.');
      }
    } catch (billingError) {
      console.error('Failed to open billing portal:', billingError);
      trackProfileEvent('fluffball-manage-subscription-failed', {
        error_message: String(billingError?.message || billingError),
      });
      setError('Could not open subscription settings right now. Please try again.');
    } finally {
      setIsManagingBilling(false);
    }
  };

  const handleActivateSubscription = async () => {
    if (!authUser || isStartingSubscription) {
      return;
    }

    const sourceDocId = String(
      userProfile?.latest_primary_result_doc_id ||
      userProfile?.latest_result_doc_id ||
      latestCompletedResultDocId ||
      (Array.isArray(userProfile?.result_doc_ids) ? userProfile.result_doc_ids[0] : '') ||
      ''
    ).trim();

    setIsStartingSubscription(true);
    setError('');
    trackProfileEvent('fluffball-profile-subscribe-clicked', {
      source_doc_id: sourceDocId,
    });
    try {
      const checkoutUrl = await openDeferredExternalUrl(async () => {
        const checkoutSession = await createSubscriptionCheckoutSession({
          docId: sourceDocId,
          userUid: authUser.uid,
          customerEmail: (authUser.email || effectiveUserProfile?.email || '').trim().toLowerCase(),
          stripeCustomerId: effectiveUserProfile?.stripe_customer_id || '',
          successUrl: `${window.location.origin}/profile?checkout=success`,
          cancelUrl: `${window.location.origin}/profile?checkout=cancel`,
        });
        return checkoutSession.url;
      });
      trackProfileEvent('fluffball-profile-subscribe-opened', {
        source_doc_id: sourceDocId,
        checkout_url_present: Boolean(checkoutUrl),
      });
    } catch (subscriptionError) {
      console.error('Failed to open subscription checkout:', subscriptionError);
      trackProfileEvent('fluffball-profile-subscribe-failed', {
        source_doc_id: sourceDocId,
        error_message: String(subscriptionError?.message || subscriptionError),
      });
      setError('Could not open subscription checkout right now. Please try again.');
    } finally {
      setIsStartingSubscription(false);
    }
  };

  const devProfilePreview = useMemo(
    () => applyDevProfileState({
      devProfileState,
      profile: userProfile,
      documents,
    }),
    [devProfileState, documents, userProfile]
  );
  const effectiveUserProfile = devProfilePreview.profile;
  const effectiveDocuments = devProfilePreview.documents;
  const effectiveSubscriptionActive = isSubscriptionActiveFromProfile(effectiveUserProfile);
  const subscriptionLabel = effectiveSubscriptionActive
    ? 'Active'
    : 'Inactive';
  const profileContentReady = Boolean(authUser) && userProfileResolved && documentsResolved;
  const activeDailyDocId = String(effectiveUserProfile?.daily_active_doc_id || '').trim();
  const activeDailyDoc = useMemo(
    () => allDocuments.find((docData) => String(docData?.id || '').trim() === activeDailyDocId) || null,
    [activeDailyDocId, allDocuments]
  );
  const activeDailyDocStatus = String(activeDailyDoc?.generation_status || '').trim().toLowerCase();
  const activeDailyDocCompletedToday = activeDailyDocStatus === 'completed' && isDailyResultFromToday(activeDailyDoc);
  const profileHomepageSnapshot = useMemo(() => {
    if (effectiveSubscriptionActive && activeDailyDocId) {
      if (['failed', 'error', 'cancelled', 'canceled'].includes(activeDailyDocStatus)) {
        return buildHomepageSnapshotFromProfile(
          authUser,
          {
            ...(effectiveUserProfile || {}),
            daily_generation_status: '',
            daily_active_doc_id: '',
            home_state: '',
            home_action_doc_id: '',
          },
          bootstrapSnapshot.homepage
        );
      }

      if (activeDailyDocCompletedToday) {
        return {
          ...buildHomepageSnapshotFromProfile(authUser, effectiveUserProfile, bootstrapSnapshot.homepage),
          heroState: 'daily_ready',
          homeActionDocId: activeDailyDocId,
        };
      }

      if (
        activeDailyDocStatus &&
        activeDailyDocStatus !== 'completed' &&
        activeDailyDocStatus !== 'completed_today'
      ) {
        return {
          ...buildHomepageSnapshotFromProfile(authUser, effectiveUserProfile, bootstrapSnapshot.homepage),
          heroState: 'daily_generating',
          homeActionDocId: activeDailyDocId,
        };
      }
    }

    return buildHomepageSnapshotFromProfile(authUser, effectiveUserProfile, bootstrapSnapshot.homepage);
  }, [
    activeDailyDocCompletedToday,
    activeDailyDocId,
    activeDailyDocStatus,
    authUser,
    bootstrapSnapshot.homepage,
    effectiveSubscriptionActive,
    effectiveUserProfile,
  ]);
  const profileHomeState = profileHomepageSnapshot.heroState;
  const profileHomeActionDocId = String(profileHomepageSnapshot.homeActionDocId || '').trim();
  const nextDailyAvailableAtMillis = getNextDailyAvailableAtMillis(effectiveUserProfile);
  const nextDailyAvailabilityLabel = formatNextDailyAvailability(nextDailyAvailableAtMillis);
  const showCreateDailyButton = effectiveSubscriptionActive && profileHomeState === 'daily_available';
  const showDailyGeneratingButton = effectiveSubscriptionActive && profileHomeState === 'daily_generating';
  const showDailyReadyMessage = effectiveSubscriptionActive && profileHomeState === 'daily_ready';
  const displayDocuments = useMemo(() => {
    if (!showDailyGeneratingButton) {
      return effectiveDocuments;
    }

    const activeDocId = String(
      activeDailyDocId || profileHomeActionDocId || ''
    ).trim();
    if (!activeDocId) {
      return effectiveDocuments;
    }

    if (allDocuments.some((docData) => String(docData?.id || '').trim() === activeDocId)) {
      return effectiveDocuments;
    }

    return [
      buildPendingDailyPlaceholderDoc(activeDocId, effectiveUserProfile),
      ...effectiveDocuments,
    ];
  }, [activeDailyDocId, allDocuments, effectiveDocuments, effectiveUserProfile, profileHomeActionDocId, showDailyGeneratingButton]);

  const totalPages = Math.max(1, Math.ceil(displayDocuments.length / PROFILE_PAGE_SIZE));
  const paginatedDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * PROFILE_PAGE_SIZE;
    return displayDocuments.slice(startIndex, startIndex + PROFILE_PAGE_SIZE);
  }, [currentPage, displayDocuments]);
  const latestCompletedResultDocId = useMemo(
    () => getLatestCompletedResultDocId(effectiveDocuments),
    [effectiveDocuments]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [displayDocuments]);

  useEffect(() => {
    if (authLoading || !profileContentReady || profileViewLogged) {
      return;
    }

    trackProfileEvent('fluffball-profile-viewed', {
      signed_in: Boolean(authUser),
      results_count: displayDocuments.length,
      dev_profile_state: devProfileState || '',
    });
    setProfileViewLogged(true);
  }, [authLoading, authUser, devProfileState, displayDocuments.length, profileContentReady, profileViewLogged, trackProfileEvent]);

  const handleStartDailyVideo = async () => {
    if (!authUser || isStartingDailyVideo) {
      return;
    }

    const fallbackResultId =
      Array.isArray(userProfile?.result_doc_ids) && userProfile.result_doc_ids.length > 0
        ? String(userProfile.result_doc_ids[0] || '').trim()
        : '';
    const sourceDocId = String(
      userProfile?.latest_primary_result_doc_id ||
      fallbackResultId ||
      userProfile?.latest_result_doc_id ||
      latestCompletedResultDocId ||
      ''
    ).trim();

    if (!sourceDocId) {
      setError('No source result is available for a daily video yet.');
      return;
    }

    setIsStartingDailyVideo(true);
    setError('');
    trackProfileEvent('fluffball-daily-video-create-clicked', {
      source_doc_id: sourceDocId,
    });
    try {
      const response = await startDailyVideoGeneration({
        userUid: authUser.uid,
        sourceDocId,
      });
      const nextDocId = String(response?.doc_id || '').trim();
      if (!nextDocId) {
        throw new Error('Missing doc_id in daily generation response');
      }
      trackProfileEvent('fluffball-daily-video-create-succeeded', {
        source_doc_id: sourceDocId,
        doc_id: nextDocId,
      });
      navigate(getModernResultPath(nextDocId));
    } catch (dailyError) {
      console.error('Failed to start daily video from profile:', dailyError);
      trackProfileEvent('fluffball-daily-video-create-failed', {
        source_doc_id: sourceDocId,
        error_message: String(dailyError?.message || dailyError),
      });
      setError('Could not start your daily video right now. Please try again.');
    } finally {
      setIsStartingDailyVideo(false);
    }
  };

  if (!authUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />

        <div className="flex items-center justify-center p-4">
          <div className="w-full max-w-2xl mt-10">
            <AuthGateStep
              createTitle="Create your account"
              error={error}
              info={authInfo}
              initialMode="sign_in"
              isLoading={isSigningIn}
              showSocialAuth={!prefersMagicLinkEmailAuth}
              emailAuthMode={
                emailLinkPending
                  ? 'magic_link_complete'
                  : (prefersMagicLinkEmailAuth ? 'magic_link_send' : 'password')
              }
              onApple={handleAppleSignIn}
              onClearError={() => {
                setError('');
                setAuthInfo('');
              }}
              onEmailCreate={handleEmailCreate}
              onEmailSignIn={handleEmailSignIn}
              onEmailLinkSend={handleEmailLinkSend}
              onEmailLinkComplete={handleEmailLinkComplete}
              onGoogle={handleGoogleSignIn}
              signInTitle="Sign in"
              subtitle={
                prefersMagicLinkEmailAuth
                  ? 'Sign in with email to see your previous generations. We\'ll send you a secure sign-in link.'
                  : 'Use Apple, Google, or email to see your previous generations.'
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (!bypassProfileGuard && authUser && !profileContentReady) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader>
          <button onClick={handleSignOut} disabled={isSigningOut} className={appHeaderActionClasses}>
            {isSigningOut ? 'Signing out...' : 'Sign out'}
          </button>
        </AppHeader>

        <div className="max-w-5xl mx-auto px-4 py-8">
          <ProfileLoadingCard />
        </div>
      </div>
    );
  }

  if (!bypassProfileGuard && authUser && userProfileResolved && !realProfileAccessible) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader>
          <button onClick={handleSignOut} disabled={isSigningOut} className={appHeaderActionClasses}>
            {isSigningOut ? 'Signing out...' : 'Sign out'}
          </button>
        </AppHeader>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader>
        <button onClick={handleSignOut} disabled={isSigningOut} className={appHeaderActionClasses}>
          {isSigningOut ? 'Signing out...' : 'Sign out'}
        </button>
      </AppHeader>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {showCreateDailyButton || showDailyGeneratingButton || showDailyReadyMessage ? (
          <div className="mb-6 rounded-3xl border border-orange-100 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-400">Daily video</p>
                {showCreateDailyButton ? (
                  <>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-800">Your next daily video is ready.</h2>
                    <p className="mt-2 text-sm text-slate-600">
                      Use today&apos;s included generation and we&apos;ll make a new cute video from your saved pet photos.
                    </p>
                  </>
                ) : null}
                {showDailyGeneratingButton ? (
                  <>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-800">Today&apos;s daily video is generating.</h2>
                    <p className="mt-2 text-sm text-slate-600">
                      Open the result page to watch progress and see it as soon as it finishes.
                    </p>
                  </>
                ) : null}
                {showDailyReadyMessage ? (
                  <>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-800">Your next daily video unlocks {nextDailyAvailabilityLabel}.</h2>
                    <p className="mt-2 text-sm text-slate-600">
                      Come back then to create it.
                    </p>
                  </>
                ) : null}
              </div>

              {showCreateDailyButton ? (
                <button
                  type="button"
                  onClick={handleStartDailyVideo}
                  disabled={isStartingDailyVideo}
                  className={primaryProfileActionClasses}
                >
                  {isStartingDailyVideo ? 'Starting...' : 'Create my daily video'}
                </button>
              ) : null}

              {showDailyGeneratingButton ? (
                <button
                  type="button"
                  onClick={() => navigate(profileHomeActionDocId ? getModernResultPath(profileHomeActionDocId) : '/profile')}
                  className={primaryProfileActionClasses}
                >
                  Watch it generate
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">{error}</div>
        ) : null}

        {devProfilePreview.bannerLabel ? (
          <div className="mb-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            Dev preview: {devProfilePreview.bannerLabel}
          </div>
        ) : null}

        {loading && displayDocuments.length === 0 ? (
          <ProfileLoadingCard />
        ) : displayDocuments.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-800">Create your first set of baby videos.</h2>
            <p className="mt-2 text-gray-600">
              Upload your pet photos and we&apos;ll make the first 3 videos for their first run.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex mt-4 px-5 py-3 rounded-full bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 text-white font-semibold shadow-sm hover:opacity-95"
            >
              Create my first 3 videos
            </button>
          </div>
        ) : (
          <>
	            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
	              {paginatedDocuments.map((docData) => {
	                const previewVideoUrl = getPreviewVideoUrl(docData);
	                const unlocked = isUnlocked(docData, effectiveSubscriptionActive);
                  const isPendingPlaceholder = Boolean(docData?.is_pending_placeholder);

	                return (
	                  <a
                    key={docData.id}
                    href={getResultPathForDocument(docData.id, docData)}
                    onClick={() => trackProfileEvent('fluffball-profile-result-opened', {
                      doc_id: docData.id,
                      generation_status: String(docData?.generation_status || '').toLowerCase(),
                      is_unlocked: unlocked,
                    })}
	                    className="group block cursor-pointer bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
	                  >
	                    <div className="bg-black">
	                      {previewVideoUrl ? (
	                        <video
                          src={previewVideoUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="metadata"
	                          className={`w-full aspect-[9/16] cursor-pointer object-cover transition-transform duration-300 group-hover:scale-[1.01] ${unlocked ? '' : 'blur-lg'}`}
	                        />
                        ) : isPendingPlaceholder ? (
                          <div className="w-full aspect-[9/16] cursor-pointer bg-gradient-to-b from-rose-50 via-orange-50 to-amber-100 flex flex-col items-center justify-center text-center px-4">
                            <div className="h-10 w-10 rounded-full border-4 border-rose-200 border-t-rose-500 animate-spin" />
                            <p className="mt-4 text-sm font-semibold text-slate-800">Generating now</p>
                            <p className="mt-2 text-xs text-slate-500">
                              Open this result to watch progress.
                            </p>
                          </div>
	                      ) : (
	                        <div className="w-full aspect-[9/16] cursor-pointer bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center text-sm text-slate-500">
	                          Preview coming soon
                        </div>
                      )}
	                    </div>
	                    <div className="p-4">
	                      <p className="text-sm font-medium text-gray-800">{formatProfileDate(docData.timestamp)}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {isPendingPlaceholder ? 'Generating' : 'Open'}
                        </p>
	                    </div>
	                  </a>
	                );
              })}
            </div>

            {totalPages > 1 ? (
              <div className="mt-8 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-white"
                >
                  Previous
                </button>
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <button
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-white"
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}

        <div className="mt-10 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-gray-400">Account</p>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-base font-semibold text-gray-800">{authUser.email || 'your account'}</p>
              <p className={`text-sm mt-1 ${effectiveSubscriptionActive ? 'text-green-700' : 'text-gray-600'}`}>
                Subscription {subscriptionLabel.toLowerCase()}
              </p>
              {effectiveUserProfile?.subscription_cancel_at_period_end ? (
                <p className="text-xs text-amber-700 mt-1">
                  Cancel scheduled at period end.
                </p>
              ) : null}
            </div>
            {effectiveSubscriptionActive && effectiveUserProfile?.stripe_customer_id ? (
              <button
                onClick={handleManageSubscription}
                disabled={isManagingBilling}
                className="text-sm font-medium text-gray-500 underline underline-offset-4 hover:text-gray-700 disabled:opacity-60"
              >
                {isManagingBilling ? 'Opening subscription settings...' : 'Manage subscription'}
              </button>
            ) : null}
          </div>
          {!effectiveSubscriptionActive ? (
            <div className="mt-5">
              <button
                type="button"
                onClick={handleActivateSubscription}
                disabled={isStartingSubscription}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isStartingSubscription ? 'Opening checkout...' : 'Turn on Fluffball Premium'}
              </button>
            </div>
          ) : null}
        </div>

        <LegalFooter className="mt-10" />
      </div>
    </div>
  );
};

export default MyResultsPage;
